import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogOut, RefreshCw, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { verifyAdmin, getIntakes, getContacts, getStats, updateStatus } from "@/lib/api";

const STORAGE_KEY = "fsls_admin_key";

function Login({ onAuth }) {
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await verifyAdmin(pass);
      localStorage.setItem(STORAGE_KEY, res.key);
      onAuth(res.key);
    } catch {
      toast.error("Invalid passcode");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-card border border-border p-10">
        <p className="font-serif text-4xl text-foreground">Staff Login</p>
        <p className="text-foreground/60 text-sm mt-2 mb-8">Fresh Start Living Services — Admin Review</p>
        <Label className="text-foreground">Passcode</Label>
        <Input data-testid="admin-passcode-input" type="password" value={pass} onChange={(e) => setPass(e.target.value)} className="mt-2 bg-input border-border" placeholder="Enter passcode" />
        <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="mt-6 w-full rounded-full bg-primary text-primary-foreground hover:bg-foreground hover:text-background transition-colors duration-300">
          {loading ? "Verifying..." : "Enter Dashboard"}
        </Button>
        <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-sm text-secondary hover:text-primary transition-colors">
          <ArrowLeft size={14} /> Back to site
        </Link>
      </form>
    </div>
  );
}

const StatCard = ({ label, value, testid }) => (
  <div className="rounded-xl bg-card border border-border p-6" data-testid={testid}>
    <p className="font-serif text-4xl text-foreground">{value}</p>
    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">{label}</p>
  </div>
);

function Dashboard({ authKey, onLogout }) {
  const [intakes, setIntakes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, contacts: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  const exportCsv = () => {
    const cols = ["full_name", "email", "phone", "age", "dependents", "housing_situation", "move_in_date", "reason", "medical_concerns", "notes", "status", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(",")].concat(
      intakes.map((r) => cols.map((c) => esc(r[c])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `intake-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Applications exported");
  };

  const visibleIntakes = intakes.filter((r) => filter === "All" || r.status === filter);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, c, s] = await Promise.all([
        getIntakes(authKey), getContacts(authKey), getStats(authKey),
      ]);
      setIntakes(i); setContacts(c); setStats(s);
    } catch {
      toast.error("Failed to load. Please re-login.");
      onLogout();
    } finally {
      setLoading(false);
    }
  }, [authKey, onLogout]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await updateStatus(authKey, id, status);
      toast.success(`Marked as ${status}`);
      setSelected((s) => (s ? { ...s, status } : s));
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div>
            <p className="font-serif text-2xl text-foreground leading-none">Admin Review</p>
            <p className="text-xs uppercase tracking-[0.3em] text-secondary mt-1">Fresh Start Living Services</p>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="admin-refresh-btn" variant="outline" onClick={load} className="rounded-full border-border">
              <RefreshCw size={16} className="mr-2" /> Refresh
            </Button>
            <Button data-testid="admin-logout-btn" onClick={onLogout} className="rounded-full bg-secondary text-secondary-foreground hover:bg-foreground transition-colors">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard label="Applications" value={stats.total} testid="stat-total" />
          <StatCard label="Pending" value={stats.pending} testid="stat-pending" />
          <StatCard label="Approved" value={stats.approved} testid="stat-approved" />
          <StatCard label="Messages" value={stats.contacts} testid="stat-contacts" />
        </div>

        <Tabs defaultValue="intakes">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="intakes" data-testid="tab-intakes">Intake Applications</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contact Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="intakes" className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-2" data-testid="status-filters">
                {["All", "Pending", "Approved"].map((f) => (
                  <button
                    key={f}
                    data-testid={`filter-${f.toLowerCase()}`}
                    onClick={() => setFilter(f)}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors duration-300 ${
                      filter === f
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-transparent text-foreground/70 border-border hover:border-primary"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <Button
                data-testid="export-csv-btn"
                variant="outline"
                onClick={exportCsv}
                disabled={intakes.length === 0}
                className="rounded-full border-border"
              >
                <Download size={16} className="mr-2" /> Export CSV
              </Button>
            </div>
            <div className="rounded-xl border border-border overflow-hidden bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="hidden md:table-cell">Housing Situation</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!loading && visibleIntakes.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-10">No applications{filter !== "All" ? ` marked ${filter}` : " yet"}.</TableCell></TableRow>
                  )}
                  {visibleIntakes.map((r) => (
                    <TableRow key={r.id} data-testid={`intake-row-${r.id}`} onClick={() => setSelected(r)} className="border-border cursor-pointer">
                      <TableCell className="font-medium text-foreground">{r.full_name}</TableCell>
                      <TableCell className="text-foreground/70">{fmt(r.created_at)}</TableCell>
                      <TableCell className="hidden md:table-cell text-foreground/70">{r.housing_situation}</TableCell>
                      <TableCell>
                        <Badge className={r.status === "Approved" ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground"}>
                          {r.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <div className="rounded-xl border border-border overflow-hidden bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="hidden md:table-cell">Message</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!loading && contacts.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-10">No messages yet.</TableCell></TableRow>
                  )}
                  {contacts.map((r) => (
                    <TableRow key={r.id} data-testid={`contact-row-${r.id}`} className="border-border">
                      <TableCell className="font-medium text-foreground">{r.name}</TableCell>
                      <TableCell className="text-foreground/70">{r.email}</TableCell>
                      <TableCell className="hidden md:table-cell text-foreground/70 max-w-xs truncate">{r.message}</TableCell>
                      <TableCell className="text-foreground/70">{fmt(r.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto" data-testid="intake-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl text-foreground">{selected.full_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm mt-2">
                {[
                  ["Email", selected.email],
                  ["Phone", selected.phone],
                  ["Age", selected.age],
                  ["Dependents", selected.dependents],
                  ["Housing situation", selected.housing_situation],
                  ["Preferred move-in", selected.move_in_date || "—"],
                  ["Reason", selected.reason],
                  ["Medical concerns", selected.medical_concerns || "—"],
                  ["Additional notes", selected.notes || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 gap-3 border-b border-border pb-2">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="col-span-2 text-foreground">{v}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-6">
                <Button data-testid="admin-approve-btn" onClick={() => changeStatus(selected.id, "Approved")} className="rounded-full bg-secondary text-secondary-foreground flex-1">
                  Approve
                </Button>
                <Button data-testid="admin-pending-btn" onClick={() => changeStatus(selected.id, "Pending")} variant="outline" className="rounded-full border-border flex-1">
                  Mark Pending
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const [authKey, setAuthKey] = useState(() => localStorage.getItem(STORAGE_KEY));

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthKey(null);
  };

  return authKey ? <Dashboard authKey={authKey} onLogout={logout} /> : <Login onAuth={setAuthKey} />;
}
